import { DEFAULT_EXCHANGE_RATES, categoryNames, normalizeCategory } from "../constants";
import { formatMonth } from "./formatters";
import { getOptionLabel } from "./i18n";

const getRate = (currencyCode, exchangeRates = DEFAULT_EXCHANGE_RATES) =>
  Number(exchangeRates[currencyCode] || DEFAULT_EXCHANGE_RATES[currencyCode] || 1);

export const toBaseAmount = (amount, currencyCode, exchangeRates) =>
  Number(amount || 0) / getRate(currencyCode, exchangeRates);

export const fromBaseAmount = (amount, currencyCode, exchangeRates) =>
  Number(amount || 0) * getRate(currencyCode, exchangeRates);

export const convertAmount = (amount, sourceCurrency, targetCurrency, exchangeRates) =>
  fromBaseAmount(toBaseAmount(amount, sourceCurrency, exchangeRates), targetCurrency, exchangeRates);

const monthKey = (date) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
};

const dateKey = (date) => {
  const parsed = new Date(date);
  return parsed.toISOString().slice(0, 10);
};

const getPeriodRange = (period) => {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  if (period === "today") {
    return { start, end };
  }

  if (period === "week") {
    const day = start.getDay() || 7;
    start.setDate(start.getDate() - day + 1);
    return { start, end };
  }

  if (period === "month") {
    start.setDate(1);
    return { start, end };
  }

  if (period === "year") {
    start.setMonth(0, 1);
    return { start, end };
  }

  return null;
};

const advanceDate = (date, frequency) => {
  const next = new Date(date);

  if (frequency === "Daily") {
    next.setDate(next.getDate() + 1);
  } else if (frequency === "Weekly") {
    next.setDate(next.getDate() + 7);
  } else if (frequency === "Monthly") {
    next.setMonth(next.getMonth() + 1);
  } else if (frequency === "Yearly") {
    next.setFullYear(next.getFullYear() + 1);
  }

  return next;
};

export const materializeRecurringExpenses = (expenses, userId, today = new Date()) => {
  const generated = [];
  const existingKeys = new Set(
    expenses
      .filter((expense) => expense.recurringKey)
      .map((expense) => expense.recurringKey)
  );

  expenses
    .filter((expense) => expense.frequency && expense.frequency !== "One-time" && !expense.parentRecurringId)
    .forEach((expense) => {
      let cursor = advanceDate(new Date(expense.date), expense.frequency);
      let guard = 0;

      while (cursor <= today && guard < 120) {
        const key = `${expense.id}:${dateKey(cursor)}`;

        if (!existingKeys.has(key)) {
          generated.push({
            ...expense,
            id: key,
            date: cursor.toISOString(),
            userId,
            parentRecurringId: expense.id,
            recurringKey: key,
            isGenerated: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
          existingKeys.add(key);
        }

        cursor = advanceDate(cursor, expense.frequency);
        guard += 1;
      }
    });

  return {
    expenses: generated.length ? [...expenses, ...generated] : expenses,
    generatedCount: generated.length
  };
};

export const enrichExpenses = (expenses, targetCurrency, exchangeRates) =>
  expenses.map((expense) => {
    const category = normalizeCategory(expense.category);

    return {
      ...expense,
      category,
      amountConverted: Number(
        convertAmount(
          expense.amount,
          expense.currency || targetCurrency,
          targetCurrency,
          exchangeRates
        ).toFixed(2)
      ),
      monthKey: monthKey(expense.date)
    };
  });

export const filterExpenses = (expenses, filters) =>
  expenses.filter((expense) => {
    const search = filters.search.trim().toLowerCase();
    const haystack = `${expense.title} ${expense.description || ""} ${expense.category} ${expense.priority} ${(expense.tags || []).join(" ")}`.toLowerCase();
    const amount = Number(expense.amountConverted || expense.amount || 0);
    const expenseDate = new Date(expense.date);
    const periodRange = getPeriodRange(filters.period);

    const matchesSearch = !search || haystack.includes(search);
    const matchesCategory = filters.category === "All" || expense.category === filters.category;
    const matchesPriority = filters.priority === "All" || expense.priority === filters.priority;
    const matchesMin = filters.minAmount == null || amount >= Number(filters.minAmount);
    const matchesMax = filters.maxAmount == null || amount <= Number(filters.maxAmount);
    const matchesPreset =
      !periodRange || (expenseDate >= periodRange.start && expenseDate <= periodRange.end);
    const matchesStart = !filters.startDate || expenseDate >= new Date(filters.startDate);
    const matchesEnd = !filters.endDate || expenseDate <= new Date(filters.endDate);
    const matchesTags =
      !filters.tags.length || filters.tags.some((tag) => (expense.tags || []).includes(tag));

    return (
      matchesSearch &&
      matchesCategory &&
      matchesPriority &&
      matchesMin &&
      matchesMax &&
      matchesPreset &&
      matchesStart &&
      matchesEnd &&
      matchesTags
    );
  });

export const getCategoryData = (expenses) => {
  const totals = categoryNames.reduce((map, category) => {
    map[category] = 0;
    return map;
  }, {});

  expenses.forEach((expense) => {
    totals[expense.category] = (totals[expense.category] || 0) + Number(expense.amountConverted || 0);
  });

  return Object.entries(totals)
    .filter(([, amount]) => amount > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([category, amount]) => ({ category, amount: Number(amount.toFixed(2)) }));
};

export const getMonthlyData = (expenses, localeCode = "en-US") => {
  const totals = {};

  expenses.forEach((expense) => {
    const key = monthKey(expense.date);
    if (!key) return;
    totals[key] = (totals[key] || 0) + Number(expense.amountConverted || 0);
  });

  return Object.keys(totals)
    .sort()
    .slice(-6)
    .map((key) => {
      const [year, month] = key.split("-");
      const sample = new Date(Number(year), Number(month) - 1, 1);
      return {
        key,
        label: formatMonth(sample, localeCode),
        value: Number(totals[key].toFixed(2))
      };
    });
};

export const getGoalProgress = (goals) =>
  goals.map((goal) => {
    const target = Number(goal.targetAmount || 1);
    const current = Number(goal.currentAmount || 0);
    const targetDate = new Date(goal.targetDate);

    return {
      ...goal,
      progress: Math.min(100, (current / target) * 100),
      daysRemaining: Math.ceil((targetDate - new Date()) / (1000 * 60 * 60 * 24))
    };
  });

export const getCategoryBudgetData = (expenses, categoryBudgets = {}) =>
  categoryNames
    .map((category) => {
      const spent = expenses
        .filter((expense) => expense.category === category)
        .reduce((sum, expense) => sum + Number(expense.amountConverted || 0), 0);
      const budget = Number(categoryBudgets[category] || 0);
      const percentage = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;

      return {
        category,
        spent: Number(spent.toFixed(2)),
        budget,
        percentage
      };
    })
    .filter((item) => item.budget > 0 || item.spent > 0);

export const getMonthlyInsights = (expenses, budget, categoryBudgets, locale = {}) => {
  const t = locale.insights || {};
  const now = new Date();
  const currentKey = monthKey(now);
  const previousDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const previousKey = monthKey(previousDate);

  const currentMonth = expenses.filter((expense) => expense.monthKey === currentKey);
  const previousMonth = expenses.filter((expense) => expense.monthKey === previousKey);
  const currentTotal = currentMonth.reduce((sum, expense) => sum + Number(expense.amountConverted || 0), 0);
  const previousTotal = previousMonth.reduce((sum, expense) => sum + Number(expense.amountConverted || 0), 0);
  const recurringTotal = currentMonth
    .filter((expense) => expense.frequency !== "One-time" || expense.parentRecurringId)
    .reduce((sum, expense) => sum + Number(expense.amountConverted || 0), 0);
  const categoryData = getCategoryData(currentMonth);
  const topCategory = categoryData[0];
  const categoryBudgetsData = getCategoryBudgetData(currentMonth, categoryBudgets);
  const exceededCategories = categoryBudgetsData.filter((item) => item.budget > 0 && item.spent > item.budget);
  const trend =
    previousTotal > 0 ? Number((((currentTotal - previousTotal) / previousTotal) * 100).toFixed(1)) : null;

  return [
    {
      title: t.monthlyPace || "Monthly pace",
      value: trend == null ? t.newBaseline || "New baseline" : `${trend > 0 ? "+" : ""}${trend}%`,
      detail:
        trend == null
          ? t.newBaselineDetail || "This month will become your comparison point."
          : trend > 0
            ? t.spendingAbove || "Spending is above last month."
            : t.spendingBelow || "Spending is below last month.",
      tone: trend != null && trend > 15 ? "warning" : "success"
    },
    {
      title: t.budgetHealth || "Budget health",
      value: budget > 0 ? `${Math.min(100, (currentTotal / budget) * 100).toFixed(0)}%` : t.notSet || "Not set",
      detail:
        budget > 0
          ? (t.leftInBudget || ((amount) => `${amount} left in the monthly budget.`))(Math.max(0, budget - currentTotal).toFixed(0))
          : t.budgetAlertsHint || "Set a monthly budget to unlock budget alerts.",
      tone: budget > 0 && currentTotal > budget ? "danger" : "success"
    },
    {
      title: t.topCategory || "Top category",
      value: topCategory ? getOptionLabel(locale, "categories", topCategory.category, topCategory.category) : t.noData || "No data",
      detail: topCategory
        ? (t.spentThisMonth || ((amount) => `${amount} spent this month.`))(topCategory.amount.toFixed(0))
        : t.addExpensesPatterns || "Add expenses to see patterns.",
      tone: "neutral"
    },
    {
      title: t.recurringLoad || "Recurring load",
      value: `${recurringTotal.toFixed(0)}`,
      detail: t.recurringDetail || "Automatically generated and repeating expenses this month.",
      tone: "neutral"
    },
    {
      title: t.categoryAlerts || "Category alerts",
      value: `${exceededCategories.length}`,
      detail:
        exceededCategories.length > 0
          ? (t.overBudget || ((categories) => `${categories} over budget.`))(
              exceededCategories
                .map((item) => getOptionLabel(locale, "categories", item.category, item.category))
                .join(", ")
            )
          : t.categoryBudgetOk || "Category budgets are currently under control.",
      tone: exceededCategories.length > 0 ? "warning" : "success"
    }
  ];
};
